import api from '@/apis/siteWebrtc'
import Vue from 'vue'

// ── WebRTC 로거 설정 (레벨 + 카테고리) ─────────────────────────
// 레벨: 선택 레벨 "이상"만 출력. 카테고리: 개별 on/off 로 노이즈 필터링.
// 모든 Peer 인스턴스가 아래 반응형 config 를 공유하므로 Debug 페이지에서
// 값을 바꾸면 (스트리밍 중이어도) 즉시 반영된다.
export const WEBRTC_LOG_LEVELS = ['off', 'error', 'warn', 'info', 'debug', 'verbose']
export const WEBRTC_LOG_CATEGORIES = ['connect', 'ice', 'dc', 'stream', 'health', 'relay', 'misc']

const LEVEL_RANK = { off: 0, error: 1, warn: 2, info: 3, debug: 4, verbose: 5 }

const LS_KEY_LEVEL = 'ckt_webrtc_log_level'
const LS_KEY_CATEGORIES = 'ckt_webrtc_log_categories'
const LS_KEY_LEGACY = 'ckt_webrtc_debug_mode' // 구버전 단일 토글 (하위호환)

export const webrtcLogConfig = Vue.observable({
  level: 'off', // 기본 비활성화. 하이드레이션 전 창에서 로그가 새는 것을 방지.
  categories: { connect: true, ice: true, dc: true, stream: true, health: true, relay: true, misc: true },
})

let _peerSeq = 0
let _hydrated = false

// 로그 메시지(첫 인자) 텍스트로 카테고리 추론. health/ice(가장 시끄러운 축)는
// 키워드로 정확히 분류되고, 나머지는 최선의 근사치로 분류된다.
function inferLogCategory (msg) {
  const s = String(msg == null ? '' : msg).toLowerCase()
  if (/health|bad path|bad streak/.test(s)) return 'health'
  if (/relay/.test(s)) return 'relay'
  if (/\bice\b|candidate|turn:443|icegather|iceconnection/.test(s)) return 'ice'
  if (/set_stream|play_stream|stop_stream|playback|play skipped|play queued|\bplay\b|\bstream\b/.test(s)) return 'stream'
  if (/datachannel|dc\[|\bdc\b|command|pong|ping/.test(s)) return 'dc'
  if (/connect|offer|answer|setremotedescription|p2p|close\(|wait_connected/.test(s)) return 'connect'
  return 'misc'
}

// Debug 페이지에서 호출: 반응형 config 갱신 + 저장소 반영.
export async function setWebrtcLogLevel (level) {
  if (LEVEL_RANK[level] == null) return
  webrtcLogConfig.level = level
  try {
    await Vue.tool.storageSetItem(LS_KEY_LEVEL, level)
    await Vue.tool.storageSetItem(LS_KEY_LEGACY, level !== 'off') // 레거시 키 동기화
  } catch (e) { console.warn('[Peer] save log level failed', e) }
}

export async function setWebrtcLogCategory (cat, enabled) {
  if (!WEBRTC_LOG_CATEGORIES.includes(cat)) return
  webrtcLogConfig.categories = { ...webrtcLogConfig.categories, [cat]: !!enabled }
  try {
    await Vue.tool.storageSetItem(LS_KEY_CATEGORIES, JSON.stringify(webrtcLogConfig.categories))
  } catch (e) { console.warn('[Peer] save log categories failed', e) }
}

// 저장소 → webrtcLogConfig 하이드레이션 (최초 1회). 레벨 키가 없으면 구버전
// boolean 토글(on→debug / off→off)을 존중한다.
export async function hydrateWebrtcLogConfig (force = false) {
  if (_hydrated && !force) return
  _hydrated = true
  try {
    const level = await Vue.tool.storageGetItem(LS_KEY_LEVEL)
    if (level && LEVEL_RANK[level] != null) {
      webrtcLogConfig.level = level
    } else {
      const legacy = await Vue.tool.storageGetItem(LS_KEY_LEGACY)
      webrtcLogConfig.level = (legacy === 'true' || legacy === true) ? 'debug' : 'off'
    }
    const catsRaw = await Vue.tool.storageGetItem(LS_KEY_CATEGORIES)
    if (catsRaw) {
      const parsed = JSON.parse(catsRaw)
      const next = { ...webrtcLogConfig.categories }
      for (const c of WEBRTC_LOG_CATEGORIES) {
        if (typeof parsed[c] === 'boolean') next[c] = parsed[c]
      }
      webrtcLogConfig.categories = next
    }
  } catch (e) {
    console.warn('[Peer] log config hydrate failed', e)
  }
}

class Peer {
  ice_servers = null
  _effective_ice_servers = null
  connection = null
  connection_type = Vue.observable({ local: null, remote: null })

  channel_command = null
  channel_video = null
  channel_audio = null
  channel_audio_bc = null

  is_connected = false
  is_requesting = false
  is_audio_connected = false
  is_video_connected = false
  is_command_connected = false
  is_audio_bc_connected = false

  my_stream = null
  _2way_relay_forced_once = false

  // ── 재생 상태 & 큐 ──────────────────────────────────────────
  _pending_resume_play = false
  _last_play_args = null              // { stime }
  _queued_play_args = null
  _last_stream_args = null            // { stream_url, media }
  _queued_stream_args = null
  _need_send_set_before_play = false
  _is_stream_playing = false
  _play_started_at = 0

  // 최종 의도/대기 플래그
  _want_playing = false              // 마지막 사용자 최종 의도 (play=true / stop=false)
  _pending_stop = false              // command DC 열리면 stop부터 보내야 하는지

  // ── 디버깅 ─────────────────────────────────────────────────
  _logNS = 'Peer'
  _peerId = ++_peerSeq
  get _debug(){ return webrtcLogConfig.level !== 'off' } // 하위호환 (내부/외부 참조용)

  _logPrefix(cat){
    const now = new Date()
    const p2 = n => String(n).padStart(2, '0')
    const p3 = n => String(n).padStart(3, '0')
    const ts = `${p2(now.getHours())}:${p2(now.getMinutes())}:${p2(now.getSeconds())}.${p3(now.getMilliseconds())}`
    const el = this._connected_at ? ` +${((Date.now() - this._connected_at) / 1000).toFixed(1)}s` : ''
    return `${ts} [${this._logNS}#${this._peerId}${el}][${cat}]`
  }
  _emit(level, args){
    if(LEVEL_RANK[webrtcLogConfig.level] < LEVEL_RANK[level]) return
    const cat = inferLogCategory(args && args.length ? args[0] : '')
    if(webrtcLogConfig.categories[cat] === false) return
    const fn = level === 'error' ? console.error : level === 'warn' ? console.warn : level === 'info' ? console.info : console.log
    fn(this._logPrefix(cat), ...args)
  }
  _log(...a){ this._emit('debug', a) }
  _info(...a){ this._emit('info', a) }
  _warn(...a){ this._emit('warn', a) }
  _err(...a){ this._emit('error', a) }
  _verbose(...a){ this._emit('verbose', a) }

  // ── 헬스 모니터 ────────────────────────────────────────────
  _health_timer = null
  _stats_prev = new Map()
  _bad_streak = 0
  _connected_at = 0

  /**
   * WebRTC DataChannel 비디오 헬스 모니터 옵션
   */
  _health_opts = {
    pollIntervalMs: 5000,
    minVideoBytesDelta: 512,
    badStreakThreshold: 3,
    videoLabel: 'video',
    controlLabel: 'command',
    graceMsAfterPlay: 5000,
    includePrflxInRule2: true,
    prflxGuardMs: 3000,
    noMsgFailMs: 5000,
  }

  _video_rx_total = 0
  _video_rx_prev = 0
  _last_video_rx_ts = 0

  // ── Force relay 24h hint (단일 키 맵) ──────────────────────
  _FORCE_RELAY_TTL_MS = 24 * 60 * 60 * 1000
  _FORCE_RELAY_ROOT_KEY = 'webrtc_force_relay_map'

  // ── 외부 노출 플래그/이유/콜백 ────────────────────────────
  force_relay_active = false               // 외부 접근 가능
  force_relay_reason = null                // 'hint' | 'manual' | 'auto_detect' | null
  force_relay_meta   = null                // { triggered_rules: [...], snapshot?: {...} } | null
  _on_force_relay_change = null

  async _initDebug() {
    await hydrateWebrtcLogConfig()
    this._log('logger loaded', { level: webrtcLogConfig.level, categories: webrtcLogConfig.categories })
  }

  _set_force_relay_active(val, reason=null, extra=null){
    const changed = (this.force_relay_active!==val)||(this.force_relay_reason!==reason)
    this.force_relay_active = val
    this.force_relay_reason = val ? reason : null
    this.force_relay_meta   = val ? (extra || null) : null

    if(changed && typeof this._on_force_relay_change==='function'){
      try{
        this._on_force_relay_change({
          active:this.force_relay_active,
          reason:this.force_relay_reason,
          ...(this.force_relay_meta ? this.force_relay_meta : {})
        })
      }
      catch(e){ this._warn('on_force_relay_change error', e) }
    }
    this._log('forceRelay flag ->', {active:this.force_relay_active, reason:this.force_relay_reason, meta:this.force_relay_meta})
  }
  isForceRelayActive(){ return !!this.force_relay_active }
  getForceRelayReason(){ return this.force_relay_reason }
  getForceRelayMeta(){ return this.force_relay_meta }

  constructor({ ice_servers, my_stream, site_id, bridge_id }) {
    this.ice_servers = ice_servers
    this.my_stream = my_stream
    this.site_id = site_id
    this.bridge_id = bridge_id
    this.video_data_callback = null
    this.audio_data_callback = null
    this.command_data_callback = null
    this.connection_status_callback = null
    this.connection_type_callback = null
    this.video_channel_connected_callback = null

    this._initDebug()
  }

  // ── Storage helpers (Capacitor Preferences via Vue.tool) ───
  _peerMapKey(){ return `${this.site_id}+${this.bridge_id}` }

  async _readRelayMap(){
    try{
      const raw = await Vue.tool.storageGetItem(this._FORCE_RELAY_ROOT_KEY)
      if(!raw) return {}
      const obj = JSON.parse(raw)
      return (obj && typeof obj==='object') ? obj : {}
    }catch(e){ this._warn('read relay map failed', e); return {} }
  }
  async _writeRelayMap(map){
    try{ await Vue.tool.storageSetItem(this._FORCE_RELAY_ROOT_KEY, JSON.stringify(map)) }
    catch(e){ this._warn('write relay map failed', e) }
  }
  async _rememberForceRelayForOneDay(){
    const key = this._peerMapKey()
    const map = await this._readRelayMap()
    map[key] = { until: Date.now() + this._FORCE_RELAY_TTL_MS }
    await this._writeRelayMap(map)
    this._log('forceRelay hint stored(24h)', { key, until: map[key].until })
  }
  async _loadForceRelayHint(){
    const key = this._peerMapKey()
    const map = await this._readRelayMap()
    const rec = map[key]
    if(!rec || !rec.until) return false
    const valid = Date.now() < rec.until
    if(!valid){ delete map[key]; await this._writeRelayMap(map) }
    this._log('forceRelay hint loaded', { key, valid })
    return valid
  }
  async clear_force_relay_hint(){
    const key = this._peerMapKey()
    const map = await this._readRelayMap()
    if(map[key]){ delete map[key]; await this._writeRelayMap(map); this._log('forceRelay hint cleared', { key }) }
  }

  // ── ICE helpers ────────────────────────────────────────────
  _normalizeIceServers(iceServers){
    if(!Array.isArray(iceServers)) return []
    const toArr = v => Array.isArray(v) ? v : (v ? [v] : [])
    return iceServers.map(s=>{
      if(!s) return null
      const urlsArr = toArr(s.urls).filter(u => typeof u==='string' && u.trim().length>0)
      if(urlsArr.length===0) return null
      return { ...s, urls: urlsArr }
    }).filter(Boolean)
  }
  _filterTurn443(iceServers,{keepStun=true}={}){
    if(!Array.isArray(iceServers)) return null
    const toArray = v => Array.isArray(v) ? v : (v ? [v] : [])
    const out=[]
    for(const s of iceServers){
      if(!s) continue
      const urls = toArray(s.urls)
      const kept = []
      for(const u of urls){
        if(typeof u!=='string') continue
        const url = u.trim()
        const low = url.toLowerCase()
        if(low.startsWith('stun:')){ if(keepStun) kept.push(url); continue }
        if(low.startsWith('turn:')){
          const has443 = /:(443)(\b|[/?#])/i.test(url)
          const hasTcp = /[?&]transport=tcp\b/i.test(url)
          if(has443 && hasTcp) kept.push(url)
          continue
        }
        if(low.startsWith('turns:')){
          const has443 = /:(443)(\b|[/?#])/i.test(url)
          if(has443) kept.push(url)
          continue
        }
      }
      if(kept.length>0) out.push({...s, urls: kept})
    }
    return out.length>0 ? out : null
  }

  // ── Connect ────────────────────────────────────────────────
  async connect({ type, way, forceTurn443=false } = {}){
    try{
      // 24h hint 로드
      const hint = await this._loadForceRelayHint()
      if(hint){ type='relay'; way=2; forceTurn443=true; this._log('forceRelay hint active → auto relay/443') }

      // 시작시 플래그 리셋
      this._set_force_relay_active(false, null)

      this._log('connect()', { type, way, forceTurn443 })
      this.is_requesting = true

      // 초기화
      this._stop_dc_video_health_monitor()
      this._video_rx_total=0; this._video_rx_prev=0; this._last_video_rx_ts=0

      // ICE 준비
      let servers = this._normalizeIceServers(this.ice_servers)
      const shouldForce443 = (type==='relay' && way===2) || forceTurn443
      if(shouldForce443){
        // 플래그: hint면 'hint', 아니면 'manual'
        this._set_force_relay_active(true, hint ? 'hint' : 'manual')
        const filtered = this._filterTurn443(servers, { keepStun:true })
        if(filtered){ servers = this._normalizeIceServers(filtered); this._log('ICE filtered → TURN:443 only', servers) }
        else{ this._warn('TURN:443 filter empty; fallback to normalized originals') }
      }
      this._effective_ice_servers = servers

      const rtcOpts = { iceServers: servers }
      if(type==='relay' && way===2) rtcOpts.iceTransportPolicy = 'relay'

      this.connection = new RTCPeerConnection(rtcOpts)
      this._set_on_handlers()
      this._set_data_channels()

      await this._request_p2p({ type, forceTurn443: shouldForce443 })
      await this._wait_connected()
      await this._check_connection_type()
      this.is_requesting = false
      this._log('connect() done')
    }catch(err){
      this._err('connect() error', err)
      this.is_requesting = false
    }
  }

  async close(){
    this._log('close()')
    this._stop_dc_video_health_monitor()
    if(!this.connection) return
    this.connection.close()
    if(this.connection_status_callback) this.connection_status_callback("closed_by_client")
    this.connection = null
    this.is_connected = false
    this.is_requesting = false
  }

  // ── Command helpers ────────────────────────────────────────
  send_command(data){
    if(!this.is_connected || !this.channel_command || !this.is_command_connected){
      this._warn('send_command skipped', {is_connected:this.is_connected, hasDC:!!this.channel_command, dcOpen:this.is_command_connected})
      return
    }
    try{ this._log('send_command ->', data); this.channel_command.send(data) }
    catch(err){ this._err('send_command error', err) }
  }

  // ── High level: set → play 한번에 ─────────────────────────
  start_stream({ stream_url, media, stime } = {}){
    if(stream_url && media){
      this._last_stream_args = { stream_url, media }
      this._need_send_set_before_play = true
    }
    this._last_play_args = { stime }
    this._want_playing = true           // ★ 의도: play
    this._is_stream_playing = true
    this._play_started_at = Date.now()

    if(this._last_stream_args && this._need_send_set_before_play){
      this._send_or_queue_set_stream(this._last_stream_args.stream_url, this._last_stream_args.media)
    }
    this._send_or_queue_play(stime)
  }

  // ── set_stream / play_stream ───────────────────────────────
  set_stream(stream_url, media){
    this._log('set_stream(req)', { media })
    this._last_stream_args = { stream_url, media }
    this._need_send_set_before_play = true
    this._send_or_queue_set_stream(stream_url, media)
  }
  _send_or_queue_set_stream(stream_url, media){
    if(!this.is_command_connected){
      this._log('set_stream queued: command DC not open')
      this._queued_stream_args = { stream_url, media }
      return
    }
    const req = { request_type:'stream_setup', request:{ stream_url, media } }
    this._log('set_stream(send)')
    this.send_command(JSON.stringify(req))
    this._queued_stream_args = null
    this._need_send_set_before_play = false
  }

  play_stream(stime){
    this._log('play_stream(req)', { stime })
    this._want_playing = true           // ★ 의도: play
    this._is_stream_playing = true
    this._last_play_args = { stime }
    this._play_started_at = Date.now()

    if(this._last_stream_args && this._need_send_set_before_play){
      this._send_or_queue_set_stream(this._last_stream_args.stream_url, this._last_stream_args.media)
    }
    this._send_or_queue_play(stime)
  }
  _send_or_queue_play(stime){
    if(!this._want_playing){
      this._log('play skipped: want_playing=false')
      return
    }
    if(!this.is_command_connected){ this._log('play queued: command DC not open'); this._queued_play_args={stime}; return }
    if(this._need_send_set_before_play){ this._log('play queued: need set_stream first'); this._queued_play_args={stime}; return }
    if(!this.is_video_connected){ this._log('play queued: video DC not open'); this._queued_play_args={stime}; return }

    const req = { request_type:'stream_play' }
    if(stime) req.request = { stime }
    this._log('play_stream(send)', req)
    this.send_command(JSON.stringify(req))
    this._maybe_start_health_after_play()
    this._queued_play_args = null
  }

  stop_stream(){
    this._log('stop_stream')

    // 최종 의도는 stop
    this._want_playing = false
    this._pending_resume_play = false
    this._last_play_args = null
    this._queued_play_args = null   // 연결 후 오발사 방지

    if (this.is_command_connected) {
      this.send_command(JSON.stringify({ request_type:'stream_stop' }))
      this._is_stream_playing = false
      this._stop_dc_video_health_monitor()
    } else {
      // 아직 command DC가 안 열렸으면, 열린 직후 stop부터 보냄
      this._pending_stop = true
    }
  }

  play_playback(stime){
    if(!stime) return
    this._log('play_playback', { stime })
    this._want_playing = true
    this.send_command(JSON.stringify({ request_type:'stream_request', request:{ request:'play', stime:`${stime}`, speed:'1' } }))
    this._is_stream_playing = true
    this._last_play_args = { stime }
    this._play_started_at = Date.now()
    this._maybe_start_health_after_play()
  }

  pause_playback(){
    this._log('pause_playback')
    this._want_playing = false
    this.send_command(JSON.stringify({ request_type:'stream_request', request:{ request:'pause' } }))
    this._is_stream_playing = false
    this._pending_resume_play = false
    this._queued_play_args = null
    this._stop_dc_video_health_monitor()
  }

  get_client(){
    this._log('get_client')
    this.send_command(JSON.stringify({ request_type:'stream_request', request:{ request:'get_client' } }))
  }

  request_proxy(uri, trackingId){
    this._log('request_proxy', { uri, trackingId })
    const req = { request_type:'http_proxy', request:{ uri:`${uri}`, method:'GET' } }
    if(trackingId) req['tracking_id'] = trackingId
    this.send_command(JSON.stringify(req))
  }

  // ── Audio BC ───────────────────────────────────────────────
  send_audio_bc(data){
    if(!this.is_connected || !this.channel_audio_bc || !this.is_audio_bc_connected){
      this._warn('send_audio_bc skipped', {is_connected:this.is_connected, hasDC:!!this.channel_audio_bc, dcOpen:this.is_audio_bc_connected})
      return
    }
    try{ this.channel_audio_bc.send(data) }catch(err){ this._err('send_audio_bc error', err) }
  }

  // ── Callback setters ───────────────────────────────────────
  set_on_audio_data_callback(cb){ this.audio_data_callback = cb }
  set_on_video_data_callback(cb){ this.video_data_callback = cb }

  // 레거시 단일 핸들러(호환용). 내부에선 버스와 함께 동작.
  set_on_command_data_callback(cb){
    this.command_data_callback = cb
    return 'legacy'
  }
  set_on_connection_status_callback(cb){ this.connection_status_callback = cb }
  set_on_connection_type_callback(cb){ this.connection_type_callback = cb }
  set_on_video_channel_connected_callback(cb){ this.video_channel_connected_callback = cb }
  set_on_force_relay_change(cb){ this._on_force_relay_change = cb }

  // ── Command 퍼브/섭 버스 ───────────────────────────────────
  _command_bus = new Map()       // token -> cb
  _command_bus_seq = 1
  add_command_listener(cb){
    const token = this._command_bus_seq++
    this._command_bus.set(token, cb)
    return token
  }
  remove_command_listener(token){
    this._command_bus.delete(token)
  }
  _emit_command_to_listeners(payload){
    const listeners = Array.from(this._command_bus.values())
    for(const cb of listeners){
      try{ cb(payload) }catch(e){ this._warn('command listener error', e) }
    }
  }

  // ── Wait connected ─────────────────────────────────────────
  async _wait_connected(timeout=10000){
    this._log('_wait_connected', { timeout })
    return new Promise((resolve,reject)=>{
      if(!this.connection) return reject(new Error('no peer connection'))
      if(this.is_connected) return resolve()
      const onChange = () => {
        this.is_connected = this.connection.connectionState === 'connected'
        this._log('connectionstatechange', this.connection.connectionState)
        if(this.is_connected){ cleanup(); resolve() }
      }
      const cleanup = () => {
        this.connection.removeEventListener?.('connectionstatechange', onChange)
        clearTimeout(timer)
      }
      this.connection.addEventListener?.('connectionstatechange', onChange)
      const timer = setTimeout(()=>{ cleanup(); reject(new Error('timeout')) }, timeout)
    })
  }

  // ── Relay reconnect (single path) ──────────────────────────
  async _reconnect_webrtc_force_to_2way_to_relay(){
    if(this._2way_relay_forced_once){ this._warn('relay reconnect skipped: already forced once'); return false }
    this._log('reconnect → 2-way relay (TURN:443)')

    await this._rememberForceRelayForOneDay()
    this._set_force_relay_active(true, 'auto_detect')

    this._pending_resume_play = this._is_stream_playing
    try{ this._stop_dc_video_health_monitor(); await this.close() }
    catch(e){ this._err('reconnect close error', e) }

    await this.connect({ type:'relay', way:2, forceTurn443:true })
    this._2way_relay_forced_once = true
  }

  // ── Bridge ping after command open ─────────────────────────
  _try_ping_to_bridge_webrtc(){
    this._log('_try_ping_to_bridge_webrtc')
    return new Promise((resolve,reject)=>{
      const trackingId = this.bridge_id + "_" + Date.now()

      const token = this.add_command_listener((e)=>{
        if(e?.response_type==='http_proxy' && (e.tracking_id+'')===trackingId){
          this._log('command pong ✅', e)
          clearTimeout(timer)
          this.remove_command_listener(token)
          resolve()
        }
      })

      this.request_proxy('ckbapiv2/', trackingId)

      const timeoutMs = 5000
      const timer = setTimeout(async ()=>{
        this._warn('command ping timeout → try 2-way relay')
        this.remove_command_listener(token)
        await this._rememberForceRelayForOneDay()
        this._set_force_relay_active(true, 'auto_detect', { triggered_rules: ['ping_timeout'] })
        if(!this._2way_relay_forced_once) this._reconnect_webrtc_force_to_2way_to_relay()
        reject(new Error('ping_timeout'))
      }, timeoutMs)
    })
  }

  // ── RTCPeerConnection handlers ─────────────────────────────
  _set_on_handlers(){
    if(!this.connection) return
    this.connection.onicegatheringstatechange = () => {
      this._log('iceGatheringState:', this.connection.iceGatheringState)
      if(this.connection_status_callback) this.connection_status_callback(this.connection.iceGatheringState)
    }
    this.connection.oniceconnectionstatechange = () => {
      this._log('iceConnectionState:', this.connection.iceConnectionState)
      if(this.connection_status_callback) this.connection_status_callback(this.connection.iceConnectionState)
      switch(this.connection.iceConnectionState){
        case 'disconnected':
        case 'failed':
        case 'closed':
          this.is_connected = false
          break
      }
    }
    this.connection.onicecandidate = (e) => { if(e?.candidate) this._log('candidate:', e.candidate.candidate) }
    this.connection.onicecandidateerror = (e) => this._warn('candidateError:', e.errorCode, e.url)
    this.connection.onconnectionstatechange = () => {
      this._log('connectionState:', this.connection.connectionState)
      if(this.connection_status_callback) this.connection_status_callback(this.connection.connectionState)
      switch(this.connection.connectionState){
        case 'connected':
          this.is_connected = true
          this._connected_at = Date.now()
          break
        case 'disconnected':
        case 'failed':
        case 'closed':
          this.is_connected = false
          break
      }
    }
  }

  // ── DataChannels ───────────────────────────────────────────
  _set_data_channels(){
    if(!this.connection) return
    this.channel_command  = this._new_data_channel('command')
    this.channel_video    = this._new_data_channel('video')
    this.channel_audio    = this._new_data_channel('audio')
    this.channel_audio_bc = this._new_data_channel('audio_bc')
  }

  _new_data_channel(label){
    if(!this.connection) return
    const channel = this.connection.createDataChannel(label)
    if(label==='video' || label==='audio' || label==='audio_bc'){
      try{ channel.binaryType='arraybuffer' }catch{_}
    }
    this._log(`createDataChannel('${label}')`, { binaryType: channel.binaryType })

    channel.onmessage = async (event)=>{
      switch(label){
        case 'command':
          try{
            let data = event.data
            if(data instanceof ArrayBuffer){ data = new TextDecoder().decode(new Uint8Array(data)) }
            else if(data instanceof Blob){ const buf = await data.arrayBuffer(); data = new TextDecoder().decode(new Uint8Array(buf)) }
            const json = JSON.parse(data)

            // 요청(send_command ->)과 대칭으로, 수신한 command 응답 본문도 로깅.
            // status_code가 있고 2xx가 아니면 error 레벨로 찍어서, 로그 레벨을
            // error로 좁혀도 실패 응답은 보이게 한다.
            // 브릿지가 숫자를 문자열("500")로 보내는 경우가 있어 숫자로 강제 변환한다.
            const _scRaw = json == null ? undefined : json.status_code
            const _sc = (_scRaw === null || _scRaw === undefined || _scRaw === '') ? NaN : Number(_scRaw)
            if (Number.isFinite(_sc) && (_sc < 200 || _sc >= 300)) {
              this._err(`command(recv) ❌ ${_sc}`, json)
            } else {
              this._log('command(recv) <-', json)
            }

            // ① 레거시 단일 핸들러
            if(this.command_data_callback){ this.command_data_callback(json) }
            // ② 버스 구독자 모두에게 브로드캐스트
            this._emit_command_to_listeners(json)
          }catch(err){ this._err('command parse error', err) }
          break
        case 'video':
          this._video_rx_total++
          this._last_video_rx_ts = Date.now()
          if(this.video_data_callback) this.video_data_callback(event)
          break
        case 'audio':
          if(this.audio_data_callback) this.audio_data_callback(event)
          break
        case 'audio_bc':
          break
      }
    }

    channel.onopen = ()=>{
      this._log(`dc[${label}] open`)
      if(label==='audio') this.is_audio_connected = true

      if(label==='video'){
        this.is_video_connected = true
        if(this.video_channel_connected_callback) this.video_channel_connected_callback(true)

        // 의도가 play일 때만 queued play 발사
        if(this._want_playing && this._is_stream_playing && this._queued_play_args && !this._need_send_set_before_play){
          this._log('video DC open → send queued play', this._queued_play_args)
          this._send_or_queue_play(this._queued_play_args.stime)
          this._pending_resume_play = false
        }

        // 연결 중 play 요청이 있었던 케이스 재검수 루틴
        if (this._want_playing) {
          this._log('video DC open → delayed health check & ping reverify')
          this._try_ping_to_bridge_webrtc().catch(()=>{})
          this._check_connection_type().catch(()=>{})
          this._maybe_start_health_after_play()
        }
      }

      if(label==='audio_bc') this.is_audio_bc_connected = true

      if(label==='command'){
        this.is_command_connected = true
        this._try_ping_to_bridge_webrtc()

        // stop이 대기 중이면 stop 우선 처리
        if (this._pending_stop) {
          this._log('command DC open → flush pending STOP first')
          this.send_command(JSON.stringify({ request_type:'stream_stop' }))
          this._pending_stop = false
          this._is_stream_playing = false
          this._stop_dc_video_health_monitor()
        }

        if(this._pending_resume_play && this._is_stream_playing && this._want_playing){
          this._log('resume flagged after relay reconnect')
          if(this._last_stream_args){
            this._need_send_set_before_play = true
            this._send_or_queue_set_stream(this._last_stream_args.stream_url, this._last_stream_args.media)
          }
          this._queued_play_args = this._last_play_args || this._queued_play_args
        }

        if(this._queued_stream_args && this._want_playing){
          const { stream_url, media } = this._queued_stream_args
          this._send_or_queue_set_stream(stream_url, media)
        }
        if(this._queued_play_args && !this._need_send_set_before_play && this.is_video_connected && this._want_playing){
          this._send_or_queue_play(this._queued_play_args.stime)
        }
      }
    }

    channel.onclose = ()=>{
      this._warn(`dc[${label}] close`)
      if(label==='audio') this.is_audio_connected = false
      if(label==='video'){
        this.is_video_connected = false
        if(this.video_channel_connected_callback) this.video_channel_connected_callback(false)
        this._stop_dc_video_health_monitor()
      }
      if(label==='audio_bc') this.is_audio_bc_connected = false
      if(label==='command') this.is_command_connected = false
    }

    channel.onerror = (e)=> this._err(`dc[${label}] error`, e)
    return channel
  }

  // ── ICE gather ─────────────────────────────────────────────
  async _gather_all_candidates(){
    this._log('_gather_all_candidates')
    return new Promise((resolve,reject)=>{
      if(!this.connection) return reject(new Error('no peer connection'))
      const onIce = (e)=>{
        if(e.candidate===null){
          this.connection.removeEventListener?.('icecandidate', onIce)
          resolve()
        }
      }
      this.connection.addEventListener?.('icecandidate', onIce)
    })
  }

  // ── Offer/Answer (서버로 ICE/forceTurn443 전달) ────────────
  async _request_p2p({ type, forceTurn443=false } = {}){
    this._log('_request_p2p', { type, forceTurn443 })
    await this.connection.setLocalDescription(await this.connection.createOffer())
    await this._gather_all_candidates()
    const offer = this.connection.localDescription

    const payload = {
      siteId: this.site_id,
      bridgeId: this.bridge_id,
      iceServers: this._effective_ice_servers || this._normalizeIceServers(this.ice_servers),
      myStream: this.my_stream,
      offer
    }
    if(type) payload.type = type
    if(forceTurn443) payload.forceTurn443 = true

    this._log('send offer → server', { hasForceTurn443: !!payload.forceTurn443 })
    const response = await api.createWebrtcPeer(payload)
    const answer = await response.json()
    this._log('answer received → setRemoteDescription')
    await this.connection.setRemoteDescription(answer)
  }

  // ── Path type & force flag auto-off(AND 조건) ──────────────
  async _check_connection_type(){
    if(!this.connection) return
    const stats = await this.connection.getStats()

    let selectedPair = null
    for(const r of stats.values()){
      if(r.type==='transport' && r.selectedCandidatePairId){
        selectedPair = stats.get(r.selectedCandidatePairId); break
      }
    }
    if(!selectedPair){
      for(const r of stats.values()){
        if(r.type==='candidate-pair' && r.state==='succeeded' && r.nominated){ selectedPair=r; break }
      }
    }
    if(!selectedPair){ this._log('_check_connection_type: no selected pair'); return }

    const localCandidate  = stats.get(selectedPair.localCandidateId)
    const remoteCandidate = stats.get(selectedPair.remoteCandidateId)
    if(!localCandidate || !remoteCandidate){
      this._log('_check_connection_type: missing candidate details', { localOk:!!localCandidate, remoteOk:!!remoteCandidate })
      return
    }

    const local  = localCandidate.candidateType // 'host' | 'srflx' | 'prflx' | 'relay'
    const remote = remoteCandidate.candidateType

    Vue.set(this.connection_type, 'local', local)
    Vue.set(this.connection_type, 'remote', remote)
    if(this.connection_type_callback) this.connection_type_callback(this.connection_type)
    this._log('_check_connection_type', { local, remote })

    const isNonRelay = t => t==='host' || t==='srflx' || t==='prflx'
    const normalPath = isNonRelay(local) && isNonRelay(remote)

    // 정상 경로면 강제 릴레이 플래그/메타 OFF
    if(normalPath && this.force_relay_active){
      this._set_force_relay_active(false, null)
    }
  }

  // ── Health monitor ─────────────────────────────────────────
  _maybe_start_health_after_play(){
    if(this._want_playing && this._is_stream_playing && this.is_video_connected && !this._health_timer){
      this._start_dc_video_health_monitor()
    }
  }
  _start_dc_video_health_monitor(){
    if(!this.connection || this._health_timer) return
    this._bad_streak=0; this._stats_prev?.clear?.(); this._video_rx_prev=this._video_rx_total
    const { pollIntervalMs } = this._health_opts
    this._log('health monitor START', { pollIntervalMs })
    this._health_timer = setInterval(()=>this._health_tick().catch(e=>this._warn('health tick error', e)), pollIntervalMs)
  }
  _stop_dc_video_health_monitor(){
    if(this._health_timer){ clearInterval(this._health_timer); this._health_timer=null; this._log('health monitor STOP') }
  }
  async _health_tick(){
    if(!this.connection) return
    if(!this._is_stream_playing || !this._want_playing){ this._bad_streak=0; return }

    const { graceMsAfterPlay } = this._health_opts
    if(Date.now()-this._play_started_at < graceMsAfterPlay){ this._log('health: grace'); return }

    const stats = await this.connection.getStats()
    const prev = this._stats_prev
    const delta = (curr,prev,k)=>(curr[k]||0)-((prev&&prev[k])||0)

    const {
      videoLabel, controlLabel, minVideoBytesDelta, badStreakThreshold,
      includePrflxInRule2, prflxGuardMs, noMsgFailMs
    } = this._health_opts

    let selectedPair=null, candidateType='unknown'
    let totalBytesDelta=0
    const perDc={}

    stats.forEach(r=>{
      const p = prev.get(r.id)
      if(r.type==='transport' && r.selectedCandidatePairId) selectedPair = stats.get(r.selectedCandidatePairId)
      if(r.type==='candidate-pair' && r.state==='succeeded' && r.nominated) selectedPair = r
      if(r.type==='data-channel'){
        const label = r.label || `dc#${r.dataChannelIdentifier}`
        const rx = delta(r,p,'messagesReceived')
        const tx = delta(r,p,'messagesSent')
        const bin = delta(r,p,'bytesReceived')
        const bout = delta(r,p,'bytesSent')
        perDc[label] = { rx, tx, bytesIn:bin, bytesOut:bout, id:r.dataChannelIdentifier, state:r.state }
        totalBytesDelta += (bin + bout)
      }
      prev.set(r.id, Object.fromEntries(Object.entries(r)))
    })

    if(selectedPair){
      const remote = stats.get(selectedPair.remoteCandidateId)
      candidateType = remote?.candidateType || 'unknown'
    }

    const v = perDc[videoLabel]
    const c = perDc[controlLabel]
    if(!v){ this._log('health: waiting video DC stats...'); return }

    const msgDelta    = this._video_rx_total - this._video_rx_prev
    this._video_rx_prev = this._video_rx_total
    const sincePlay   = Date.now() - (this._play_started_at || 0)
    const noMsgForMs  = Date.now() - (this._last_video_rx_ts || this._play_started_at || 0)
    const vBytesDelta = (v?.bytesIn||0) + (v?.bytesOut||0)

    const rule4 = (sincePlay > (graceMsAfterPlay + noMsgFailMs) && msgDelta===0)

    const connectedForMs = this._connected_at ? (Date.now()-this._connected_at) : 0
    const isPrflxAllowed = includePrflxInRule2 && candidateType==='prflx' && connectedForMs >= prflxGuardMs
    const isDirectPath   = candidateType==='host' || candidateType==='srflx' || isPrflxAllowed
    const rule2 = (isDirectPath && (vBytesDelta < minVideoBytesDelta))
    const rule3 = !!c && ((c.rx + c.tx) > 0) && (msgDelta===0 || vBytesDelta < minVideoBytesDelta)

    const isBad = rule2 || rule3 || rule4
    this._bad_streak = isBad ? (this._bad_streak+1) : 0

    this._log('health tick', {
      candidateType, connectedForMs,
      vBytesDelta, totalBytesDelta,
      video:v, control:c,
      msgDelta, noMsgForMs,
      rules:{ rule2, rule3, rule4, isPrflxAllowed },
      badStreak:this._bad_streak, threshold:badStreakThreshold
    })

    if(this._bad_streak >= badStreakThreshold){
      this._warn('bad path → force reconnect TURN:443')

      const fired = []
      if (rule2) fired.push(2)
      if (rule3) fired.push(3)
      if (rule4) fired.push(4)

      this._stop_dc_video_health_monitor()
      await this._rememberForceRelayForOneDay()
      this._set_force_relay_active(true, 'auto_detect', {
        triggered_rules: fired,
        snapshot: {
          candidateType,
          vBytesDelta,
          msgDelta,
          noMsgForMs,
          connectedForMs
        }
      })
      if(!this._2way_relay_forced_once) await this._reconnect_webrtc_force_to_2way_to_relay()
    }
  }
}

export default Peer